import Intercom from '@intercom/messenger-js-sdk';
import config from 'config';
import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { getSubscription, selectUser } from '../../reducers/user.selectors';
import mappers from '../../services/tracking/propertyMappers';
import { notifyBugsnagHandledError } from '../../utils/bugsnagClient';
import useIntercom from '../../utils/useIntercom';

const IntercomButton = () => {
	const user = useSelector(selectUser);
	const subscription = useSelector(getSubscription);
	const { isIntercomVisible } = useIntercom();

	const intercomUserData = useMemo(() => {
		const data = mappers.intercom(user, subscription);
		if (!data) return undefined;
		return { ...data, created_at: data.created_at ?? undefined };
	}, [user, subscription]);

	const intercomDataKey = useMemo(
		() => (intercomUserData ? JSON.stringify(intercomUserData) : ''),
		[intercomUserData]
	);

	// One boot per visible data set: StrictMode runs the effect twice per commit.
	const bootedDataKeyRef = useRef<string | null>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (!isIntercomVisible) {
			bootedDataKeyRef.current = null;
			return;
		}
		if (bootedDataKeyRef.current === intercomDataKey) return;
		bootedDataKeyRef.current = intercomDataKey;

		try {
			Intercom({
				app_id: config.intercom.appId,
				region: config.intercom.region,
				...intercomUserData,
			});
		} catch (error) {
			notifyBugsnagHandledError(error as Error);
		}
	}, [isIntercomVisible, intercomDataKey]);

	return null;
};

export default IntercomButton;
